<template>
  <div
    class="mastercard-faq"
    data-ui="mastercard-faq"
  >
    <section>
      <fauxmo-message-banner
        v-if="showFauxmoBanner"
        :is-limited-time-offer-active="showLimitedTimeOffer"
        :is-hipcamp-offer-active="showHipcampOffer"
      />
      <mastercard-hero
        v-if="content?.lead"
        v-bind="content?.lead"
      />
    </section>
    <section
      id="faqs"
      class="mastercard-faq__main"
      data-analytics-config="exclude-all"
    >
      <frequently-asked-questions
        v-for="(faq, index) in faqs"
        :key="index"
        :faq="faq"
        data-ui="faq"
        data-analytics-config="exclude-all"
        @toggle="({action, index}) => handleFAQClick(action, index, faq.title)"
      />
    </section>
    <mastercard-sticky-cta
      v-bind="content?.stickyCta"
      :is-visible="showStickyCta"
    />
    <section class="mastercard-faq__lower">
      <div class="mastercard-faq__lower-inner">
        <giftcard-promo
          v-if="content?.footer?.giftcardPromo"
          v-bind="content?.footer?.giftcardPromo"
        />
        <terms-conditions
          v-if="termsModel"
          v-bind="termsModel"
        />
        <capital-one-footer v-bind="content?.footer?.capOneFooter" />
      </div>
    </section>
  </div>
</template>

<script>
import convertToHtml from '@rei/rich-text-renderer';
import FrequentlyAskedQuestions from '@rei/frequently-asked-questions';
import { useDynamicContent } from '@rei/cs-preview-fetch';
import FauxmoMessageBanner from '../mastercard/components/FauxmoMessageBanner.vue';
import buildStickyCtaScrollHandler from '../../utils/buildStickyCtaScrollHandler';
import analyticsMixin from '../../common/mixins/analyticsMixin';
import CapitalOneFooter from '../../common/components/capital-one-footer/CapitalOneFooter.vue';
import GiftcardPromo from '../../common/components/giftcard-promo-component/GiftcardPromo.vue';
import MastercardHero from '../../common/components/mastercard-hero-component/MastercardHero.vue';
import TermsConditions from '../../common/components/terms-conditions-component/TermsConditions.vue';
import MastercardStickyCta from '../../common/components/mastercard-sticky-cta/MastercardStickyCta.vue';

export default {
  name: 'MastercardFaq',
  components: {
    FauxmoMessageBanner,
    FrequentlyAskedQuestions,
    CapitalOneFooter,
    GiftcardPromo,
    MastercardHero,
    TermsConditions,
    MastercardStickyCta,
  },
  mixins: [
    analyticsMixin,
  ],
  props: {
    appProps: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    additionalData: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    componentModels: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const getPreviewUrl = () => '/membership/mastercard/faq/preview';
    const { content } = useDynamicContent(props, getPreviewUrl);
    if (content.value?.componentModels) {
      content.value = content.value.componentModels;
    }
    return {
      content,
    };
  },
  data() {
    return {
      showStickyCta: false,
      scrollHandler: null,
    };
  },
  computed: {
  /* c8 ignore start */
    analyticsViewData() {
      return {
        pageSection: 'faq',
        templateType: 'mastercard',
      };
    },
    featureToggles() {
      return this.appProps?.['feature.toggles'] || {};
    },
    /* c8 ignore end */
    faqs() {
      return this.content?.faqs?.map(({ title, entries }) => {
        const questionsAndAnswers = entries?.map(({ question, answer }) => ({
          question,
          answer: convertToHtml(answer),
        }));
        return {
          title,
          questionsAndAnswers,
        };
      });
    },
    frontendStrings() {
      return this.appProps?.['frontend.strings'] || {};
    },
    termsModel() {
      const {
        frontendStrings,
        showLimitedTimeOffer,
      } = this;
      return {
        ...this.content?.footer?.terms,
        limitedTimeOfferActive: showLimitedTimeOffer,
        limitedTimeOfferDisclaimer: frontendStrings?.limitedTimeOfferDisclaimer,
      };
    },
    showDoubleRewardsPromo() {
      return this.featureToggles?.showDoubleRewardsPromo;
    },
    showLimitedTimeOffer() {
      return this.showHipcampOffer || this.featureToggles?.showLimitedTimeOffer;
    },
    showFauxmoBanner() {
      return this.showDoubleRewardsPromo || this.showLimitedTimeOffer;
    },
    showHipcampOffer() {
      return this.featureToggles?.showHipcampOffer;
    },
  },
  mounted() {
    if (typeof window !== 'undefined') {
      this.setScrollHandler();
      window.addEventListener('scroll', this.scrollHandler);
    }
  },
  beforeUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  },
  methods: {
  /* c8 ignore start */
    handleFAQClick(action, index, component) {
      if (action === 'open') {
        this.metricsLink(`faq-${index}-${component}`);
      }
    },
    /* c8 ignore end */
    setScrollHandler() {
      this.scrollHandler = buildStickyCtaScrollHandler((showCta) => { this.showStickyCta = showCta; }, 'hero-cta-button', 'giftcard-promo-button');
    },
  },
};
</script>

<style lang="scss">
.mastercard-faq {
  // nested to override default styles, which are for the van image
  .hero__image-wrapper {
    padding-top: $cdr-space-two-x;
    background: 11% calc(20px + 40%)/25% no-repeat; // magic 🐉
    @include cdr-md-mq-up {
      margin-left: 0;
    }
  }
}

.mastercard-faq__main {
  padding-bottom: $cdr-space-two-x;

  // nested to override styles from the `frequently-asked-questions` package
  .heading {
    @include cdr-text-heading-sans-500;
    color: $cdr-color-text-brand;
    padding-top: $cdr-space-four-x;
    padding-bottom: $cdr-space-one-x;
  }

  .faq-container {
    padding: 0;

    .faq-container__content {
      padding-top: 0;
      padding-bottom: 0;
    }
  }
}

.mastercard-faq__lower {
  background-color: $cdr-color-background-secondary;
}

.mastercard-faq__lower-inner {
  @include cdr-container;
}
</style>
